import { useRef } from 'react';
import { AutoComplete, Button, Flex, Input, Tag, theme, Tooltip } from 'antd';
import {
	IoAdd,
	IoAddCircleOutline,
	IoCubeOutline,
	IoLocateOutline,
	IoSearch,
	IoSparkles,
} from 'react-icons/io5';

import ColorDot from '@components/modal/objectPickerModal/ColorDot';

import { formatCost, formatDuration } from './findCost';
import { Line, Note, PanelRow } from './FindPanel';
import { MIXED_NOTE } from './findSegment';
import { MAX_OBJECTS, OBJECT_COLORS } from './useSimilarityMap';

// The box's list holds models ("model:<uuid>") and the generic search of what is typed
const MODEL = 'model:';
const SEARCH = 'search:';
// An Enter this soon after a pick from the list is the Enter that picked it
const PICK_ENTER_MS = 300;
// The longest search text and object name: a few words, not a paragraph
export const MAX_TEXT_LENGTH = 120;

// Why a model is offered for the typed words (its `fit` from `POST /find/route`)
const fitNote = ({ fit, words, runs, classes }) => {
	if (!runs) return 'not in Find yet';
	const quoted = words.map(word => `“${word}”`).join(', ');
	if (fit === 'covers') return `${quoted} among what it finds`;
	if (fit === 'kind') return `finds ${classes.join(', ')}: a kind of ${quoted}`;
	if (fit === 'name' || fit === 'prefix') return null;
	return `finds ${quoted}`;
};

// The models offered for typed words: those Find can run; those it cannot only when none it can fits
const offered = models => {
	const running = models.filter(model => model.runs);
	return running.length ? running : models;
};

const ModelEntry = ({ model, note }) => (
	<Flex vertical>
		<span>{model.name}</span>
		<Note>
			{[model.basemodel ? 'Biodrone model' : 'Your model', note].filter(Boolean).join(' · ')}
		</Note>
	</Flex>
);

/**
 * The one box: type what to find, or pick a Biodrone model from its list. Typing offers the models
 * that fit the words (`route`, from `POST /find/route`) and the generic search, in the order a search
 * would take them; an empty box offers every model. Enter runs the search: our model when one fits
 * (decided when it runs, not from the list), else the generic finders. A pick from the list (click,
 * or arrows and Enter) only chooses.
 * @param {Object} [props.route] - The route of the text typed, once known
 * @param {Object[]} props.models - Every model the user may run (`GET /find/models`)
 * @param {Function} props.onChooseModel - Called with a model's uuid picked from the list
 * @param {Function} props.onChooseGeneric - Called when the generic search is picked from the list
 */
const SearchBox = ({
	text,
	onTextChange,
	route,
	models,
	onChooseModel,
	onChooseGeneric,
	onSubmit,
}) => {
	const typed = text.trim();
	// When an entry was last picked from the list: the Enter that picked it does not also run the search
	const pickedAt = useRef(0);
	const modelEntry = (model, note) => ({
		value: `${MODEL}${model.model_uuid}`,
		disabled: !model.runs,
		label: <ModelEntry model={model} note={note} />,
	});
	const ourModels = typed
		? offered(route?.text === typed ? route.models : []).map(model =>
				modelEntry(model, fitNote(model))
			)
		: models.map(model => modelEntry(model, model.runs ? null : 'not in Find yet'));
	const search = typed
		? [
				{
					value: SEARCH,
					label: (
						<Flex gap={6} align="center">
							<IoSearch />
							<span>Search for “{typed}”</span>
						</Flex>
					),
				},
			]
		: [];
	const groups = [
		{ label: 'Biodrone models', options: ourModels },
		{ label: 'Search', options: search },
	];
	if (route?.text === typed && route.top === 'generic') groups.reverse();
	return (
		<AutoComplete
			value={text}
			options={groups.filter(group => group.options.length)}
			showSearch={{ filterOption: false }}
			defaultActiveFirstOption={false}
			onChange={value => {
				if (!value?.startsWith(MODEL) && value !== SEARCH) onTextChange(value ?? '');
			}}
			onSelect={value => {
				pickedAt.current = Date.now();
				if (value === SEARCH) onChooseGeneric();
				else onChooseModel(value.slice(MODEL.length));
			}}
			onInputKeyDown={event => {
				if (event.key !== 'Enter') return;
				const input = event.target;
				// The list handles this Enter first when an entry is active
				setTimeout(() => {
					if (Date.now() - pickedAt.current <= PICK_ENTER_MS) return;
					// Out of the box, so its list closes off the panel
					input.blur();
					onSubmit();
				});
			}}
			prefix={<IoSearch />}
			allowClear
			placeholder="Search, or pick a Biodrone model"
			popupMatchSelectWidth
			maxLength={MAX_TEXT_LENGTH}
			aria-label="Search for"
		/>
	);
};

/**
 * The model the search runs, under the box: picked from the list, or ours fitting the words. ✕ lets
 * go of it: the words then go to the general search. Names no model behind the general search.
 */
const ModelChip = ({ model, typed, picked, onRelease }) => (
	<Flex vertical gap={2}>
		<Tag
			closable
			icon={<IoCubeOutline />}
			onClose={onRelease}
			style={{ alignSelf: 'flex-start' }}>
			{model.name}
		</Tag>
		<Note>
			{picked || !typed
				? 'Runs as AI Detection does, square by square.'
				: `Our model for “${typed}”. ✕ to search without it.`}
		</Note>
	</Flex>
);

/**
 * "Looking for": words ("car, house") or a Biodrone model, in one box, or objects marked on the map.
 * Each object has its examples (green on the map; several sizes of the same thing) and a name, the
 * label of its results; with several objects each has its own colour. Under the words, what the
 * project holds ("tree", "car", "boat"), to click into the search.
 * @param {Object|null} props.model - The model the search runs: picked, or ours fitting the words
 * @param {boolean} props.modelPicked - It was picked from the list, not chosen for the words
 * @param {Object|null} props.genericOver - Our model fitting the words, while the generic search was
 *   picked instead (offered back)
 * @param {boolean} [props.markDisabled] - No marking now (an area is being drawn)
 * @param {string|null} [props.wordsMode] - What the words ask for (`wordsMode`): 'segment' (roads,
 *   rivers: traced), 'mixed' (refused), 'objects' or null
 */
export const LookingFor = ({
	text,
	onTextChange,
	understood,
	parsing,
	onSubmit,
	route,
	models,
	model,
	modelPicked,
	genericOver,
	onChooseModel,
	onChooseGeneric,
	onReleaseModel,
	groups,
	activeGroup,
	marking,
	onMark,
	onAddExample,
	onAddObject,
	onGroupName,
	onCancelMarking,
	markDisabled = false,
	projectObjects,
	pickedObjects,
	onPickObject,
	wordsMode = null,
}) => {
	const { token } = theme.useToken();
	const marked = groups.filter(g => g.boxes.length);
	return (
		<PanelRow label="Looking for">
			{marked.length === 0 && (
				<SearchBox
					text={text}
					onTextChange={onTextChange}
					route={route}
					models={models}
					onChooseModel={onChooseModel}
					onChooseGeneric={onChooseGeneric}
					onSubmit={onSubmit}
				/>
			)}
			{marked.length === 0 && !model && wordsMode === 'segment' && (
				<Note>Traced as continuous lines and areas</Note>
			)}
			{marked.length === 0 && !model && wordsMode === 'mixed' && (
				<Note role="alert">{MIXED_NOTE}</Note>
			)}
			{marked.length === 0 && model && (
				<ModelChip
					model={model}
					typed={text.trim()}
					picked={modelPicked}
					onRelease={onReleaseModel}
				/>
			)}
			{marked.length === 0 && !model && genericOver && (
				<Note>
					General search.{' '}
					<Button
						type="link"
						size="small"
						onClick={() => onChooseModel(genericOver.model_uuid)}>
						Use {genericOver.name}
					</Button>
				</Note>
			)}
			{marked.length === 0 && parsing && <Note>Reading your request…</Note>}
			{marked.length === 0 && understood && (
				<Note>Read as: {understood.objects.join(' · ')}. Edit it, or press Find.</Note>
			)}
			{marked.length === 0 && !marking && !model && (
				<InThisProject
					objects={projectObjects}
					picked={pickedObjects}
					onPick={onPickObject}
				/>
			)}
			{marked.map((group, index) => (
				<Line key={index}>
					<ColorDot
						shape="round"
						size={token.fontSizeSM}
						color={
							groups.length > 1
								? OBJECT_COLORS[index % OBJECT_COLORS.length]
								: token.colorSuccess
						}
					/>
					<Input
						size="small"
						placeholder={`Object ${index + 1}, e.g. “car”`}
						value={group.name}
						maxLength={MAX_TEXT_LENGTH}
						onChange={event => onGroupName(index, event.target.value)}
						onPressEnter={onSubmit}
						aria-label={`Name of object ${index + 1}`}
					/>
					<Tooltip title="Examples of this object">
						<Note>{group.boxes.length}×</Note>
					</Tooltip>
					<Tooltip title="Mark another example of this object (another size or look)">
						<Button
							type="text"
							size="small"
							icon={<IoAdd />}
							aria-label={`Add an example of object ${index + 1}`}
							disabled={marking || markDisabled}
							onClick={() => onAddExample(index)}
						/>
					</Tooltip>
				</Line>
			))}
			{marking ? (
				<Line>
					<Note>
						{marked.length && activeGroup >= marked.length
							? 'Drag a box around the new object.'
							: 'Drag a box around one on the map.'}
					</Note>
					<Button type="text" onClick={onCancelMarking}>
						Cancel
					</Button>
				</Line>
			) : marked.length === 0 ? (
				<Button icon={<IoLocateOutline />} disabled={markDisabled} onClick={onMark}>
					Mark an example
				</Button>
			) : (
				marked.length < MAX_OBJECTS && (
					<Flex>
						<Button
							type="link"
							icon={<IoAddCircleOutline />}
							disabled={markDisabled}
							onClick={onAddObject}>
							New object
						</Button>
					</Flex>
				)
			)}
		</PanelRow>
	);
};

const HOW_MANY = { many: 'Many', some: 'Some', few: 'A few' };

/**
 * What the project holds, most common first (Claude looked at sample views of the orthophoto). A
 * click adds the word to the search, another click takes it out.
 * @param {Object} props.objects - The query: `data.objects` [{ name, how_many, length_m }], `isPending`
 * @param {Set<string>} props.picked - The words in the search now
 * @param {Function} props.onPick - Called with a word to add or take out
 */
const InThisProject = ({ objects, picked, onPick }) => {
	if (objects.isPending) return <Note>Looking through the project…</Note>;
	const list = objects.data?.objects ?? [];
	if (!list.length) return null;
	return (
		<Flex vertical gap={4}>
			<Note>In this project</Note>
			<Flex wrap gap={4}>
				{list.map(({ name, how_many: howMany, length_m: length }) => (
					<Tooltip
						key={name}
						title={`${HOW_MANY[howMany]} in the sample views${length ? ` · about ${length} m` : ''}`}>
						<Tag.CheckableTag checked={picked.has(name)} onChange={() => onPick(name)}>
							{name}
						</Tag.CheckableTag>
					</Tooltip>
				))}
			</Flex>
		</Flex>
	);
};

// The estimate's lines and how long it takes, for the tooltip of the Find button
const CostTip = ({ estimate }) => {
	const lines = (estimate.breakdown ?? []).filter(Boolean);
	const duration = formatDuration(estimate.seconds_estimate);
	if (!lines.length && !duration) return null;
	return (
		<Flex vertical>
			{lines.map(line => (
				<span key={line}>{line}</span>
			))}
			{duration && <span>{duration}</span>}
		</Flex>
	);
};

// Under the button: what starting compute adds to the next search's cost
const StartupNote = ({ estimate }) =>
	estimate?.usd_startup > 0 ? (
		<Note>Includes {formatCost(estimate.usd_startup)} to start compute</Note>
	) : null;

/**
 * The one primary action: find what the word or the examples describe, or run the model chosen, in
 * the area. When it cannot run yet it says why instead: compute is starting (or off: then it starts
 * it), or the area is too big (the map says by how much).
 * @param {string} props.worker - Compute's state: ready, starting, stopping, stopped, failed, none
 * @param {boolean} props.startQueued - A start waits for compute to finish stopping
 * @param {string} [props.modelName] - The model chosen, which runs instead of the words or examples
 * @param {Object|null} [props.estimate] - What the next search would cost (`estimate` of
 *   `POST /find/estimate`); none shown when null
 */
export const FindButton = ({
	text,
	sentence,
	parsing,
	groups,
	running,
	blockedByArea,
	worker,
	startQueued,
	modelName,
	estimate = null,
	onStartWorker,
	onFind,
}) => {
	const word = text.trim();
	const marked = groups.filter(g => g.boxes.length);
	const exampleCount = marked.reduce((n, g) => n + g.boxes.length, 0);
	const names = marked.map((g, i) => g.name.trim() || `object ${i + 1}`);
	if (worker === 'stopping') {
		return (
			<PanelRow>
				{startQueued ? (
					<Button type="primary" size="large" block loading disabled>
						Compute is stopping; starting again shortly…
					</Button>
				) : (
					<Button type="primary" size="large" block onClick={onStartWorker}>
						Start compute
					</Button>
				)}
			</PanelRow>
		);
	}
	if (worker === 'starting') {
		return (
			<PanelRow>
				<Button type="primary" size="large" block loading disabled>
					Starting compute…
				</Button>
			</PanelRow>
		);
	}
	if (worker !== 'ready') {
		return (
			<PanelRow>
				<Button type="primary" size="large" block onClick={onStartWorker}>
					Start compute
				</Button>
				{formatCost(estimate?.usd_total) && (
					<Note>
						Next search about {formatCost(estimate.usd_total)}
						{estimate.usd_startup > 0 &&
							`, including ${formatCost(estimate.usd_startup)} to start compute`}
					</Note>
				)}
			</PanelRow>
		);
	}
	const label = blockedByArea
		? 'Zoom in or draw a smaller area'
		: modelName
			? `Run ${modelName}`
			: marked.length > 1
				? `Find all: ${names.join(', ')}`
				: exampleCount
					? exampleCount > 1
						? `Find all like these ${exampleCount}`
						: 'Find all like this'
					: word && !sentence
						? `Find “${word}”`
						: 'Find';
	// "Find · $0.42": what this search would cost, once known; never in the way of finding
	const cost = blockedByArea || running ? null : formatCost(estimate?.usd_total);
	return (
		<PanelRow>
			<Tooltip title={cost ? <CostTip estimate={estimate} /> : null}>
				<Button
					type="primary"
					size="large"
					block
					icon={blockedByArea ? undefined : <IoSparkles />}
					loading={running || parsing}
					disabled={blockedByArea || (!modelName && !exampleCount && !word)}
					onClick={onFind}>
					{cost ? `${label} · ${cost}` : label}
				</Button>
			</Tooltip>
			{cost && <StartupNote estimate={estimate} />}
		</PanelRow>
	);
};
